import React from 'react';
import { exportLogsAsText, getLogEntries, clearLogs, getLogStats } from '../utils/devTabLogger';

interface ActionLogModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ActionLogModal: React.FC<ActionLogModalProps> = ({ isOpen, onClose }) => {
  const [logText, setLogText] = React.useState('');
  const [copied, setCopied] = React.useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = React.useState(false);
  
  React.useEffect(() => {
    if (isOpen) {
      setLogText(exportLogsAsText());
      setCopied(false);
      setShowDownloadMenu(false);
    }
  }, [isOpen]);
  
  // Listen for log updates to refresh the display while modal is open
  React.useEffect(() => {
    if (!isOpen) return;
    
    const handleLogUpdate = () => {
      setLogText(exportLogsAsText());
    };
    
    window.addEventListener('devtab-log-updated', handleLogUpdate);
    
    return () => {
      window.removeEventListener('devtab-log-updated', handleLogUpdate);
    };
  }, [isOpen]);
  
  // Close download menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showDownloadMenu && !(event.target as Element).closest('.relative')) {
        setShowDownloadMenu(false);
      }
    };
    
    if (showDownloadMenu) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDownloadMenu]);
  
  if (!isOpen) return null;
  
  const stats = getLogStats();
  
  const handleCopy = () => {
    navigator.clipboard.writeText(logText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleDownloadTXT = () => {
    const text = exportLogsAsText();
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `devtab-action-log-${new Date().toISOString().split('T')[0]}.txt`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
    setShowDownloadMenu(false);
  };
  
  const handleDownloadCSV = () => {
    const logs = getLogEntries();
    if (logs.length === 0) {
      alert('No logs to export');
      setShowDownloadMenu(false);
      return;
    }
    
    // Convert logs to CSV format
    const headers = ['Timestamp', 'Action', 'Type', 'ID', 'QBO ID', 'Success', 'Matches', 'Mismatches', 'Total Checked', 'Message', 'Details', 'Error'];
    const csvRows = [headers.map(h => `"${h}"`).join(',')];
    
    logs.forEach(log => {
      const row = [
        log.timestamp,
        log.action,
        log.type,
        log.id,
        log.qboId || '',
        log.success ? 'YES' : 'NO',
        log.matches !== undefined ? log.matches : '',
        log.mismatches !== undefined ? log.mismatches : '',
        log.totalChecked !== undefined ? log.totalChecked : '',
        log.message || '',
        log.details ? log.details.replace(/"/g, '""').replace(/\n/g, '; ') : '',
        log.error || ''
      ];
      csvRows.push(row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
    });
    
    const csv = '\uFEFF' + csvRows.join('\n'); // Add BOM for UTF-8
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `devtab-action-log-${new Date().toISOString().split('T')[0]}.csv`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
    setShowDownloadMenu(false);
  };
  
  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear all action logs? This cannot be undone.')) {
      clearLogs();
      setLogText(exportLogsAsText());
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">DevTab Action Log</h2>
            <div className="text-xs text-gray-500 mt-1">
              Total: {stats.total} | Success: {stats.successful} | Failed: {stats.failed}
              {Object.keys(stats.byType).length > 0 && (
                <span className="ml-2">
                  ({Object.entries(stats.byType).map(([type, count]) => `${type}: ${count}`).join(', ')})
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Log Content */}
        <div className="flex-1 overflow-auto p-4">
          <pre className="bg-gray-50 p-4 rounded border text-xs font-mono whitespace-pre-wrap break-words">
            {logText}
          </pre>
        </div>
        
        {/* Footer Actions */}
        <div className="flex justify-between items-center p-4 border-t bg-gray-50">
          <div className="flex gap-2">
            <button
              onClick={handleCopy}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              {copied ? (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy to Clipboard
                </>
              )}
            </button>
            
            <div className="relative">
              <button
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Log
                <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showDownloadMenu && (
                <div className="absolute right-0 bottom-full mb-1 bg-white border border-gray-300 rounded shadow-lg z-50 min-w-[140px]" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDownloadCSV();
                    }}
                    className="w-full px-3 py-2 text-left text-xs hover:bg-gray-100 flex items-center gap-2 border-b border-gray-200"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download CSV
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDownloadTXT();
                    }}
                    className="w-full px-3 py-2 text-left text-xs hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download TXT
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <button
            onClick={handleClear}
            className="px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Clear Log
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActionLogModal;

